import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { App } from '@capacitor/app'
import { nextTick, ref, watch } from 'vue'

import type { CompletionPopupData } from '@/models/completion'
import { Mixpanel } from '@/analytics/mixpanel'
import { backend } from '@/services/backend'
import { completionService, type GuideCompletion } from '@/services/completion'
import { Sentry } from '@/services/sentry'
import CompletionPopup from './CompletionPopup.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'CompletionPopupHost',
  setup(__props) {

const data = ref<CompletionPopupData | null>(null)

// Load the popup data when the completion service says it is ready to show.
// Clearing data also closes the popup when that completion is dismissed or replaced.
watch(
  () => completionService.current,
  (completion) => {
    data.value = null
    if (completion) {
      void load(completion)
    }
  },
  { immediate: true },
)

/**
 * Fetch the selected guide before opening the popup. If loading fails, discard
 * the popup because it has no useful content to show.
 */
async function load(completion: GuideCompletion): Promise<void> {
  try {
    const payload = await backend.getCompletionPopupData(completion.docId)
    // Ignore this response if another completion replaced it while loading.
    if (completionService.current !== completion) {
      return
    }
    data.value = payload
    await nextTick()
    await markShownWhenVisible(completion)
  } catch (error_) {
    Sentry.captureException(error_)
    if (completionService.current === completion) {
      completionService.dismiss()
    }
  }
}

/**
 * Stop persisting the popup after the user has had an opportunity to see it.
 * Audio can finish while the app is in the background, even though its popup
 * is loaded and rendered immediately.
 */
async function markShownWhenVisible(
  completion: GuideCompletion,
): Promise<void> {
  let acknowledged = false

  const acknowledge = (): void => {
    if (acknowledged) {
      return
    }
    acknowledged = true
    void listener.remove()

    if (completionService.current === completion) {
      Mixpanel.trackCompletionFlowShown(
        completion.docId,
        completion.completionPath,
      )
      completionService.markShown()
    }
  }
  const listener = await App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      acknowledge()
    }
  })

  const { isActive } = await App.getState()
  if (isActive) {
    acknowledge()
  }
}

return (_ctx: any,_cache: any) => {
  return (data.value)
    ? (_openBlock(), _createBlock(CompletionPopup, {
        key: 0,
        data: data.value,
        onDismiss: _cache[0] || (_cache[0] = ($event: any) => (_unref(completionService).dismiss()))
      }, null, 8, ["data"]))
    : _createCommentVNode("", true)
}
}

})